import { formatHours } from '../../util/hoursFormat';
import { money, tenantTimeZone } from './clockParse';

/**
 * Change-log value / actor formatting shared by the HistoryPanel (M2-B3) and the
 * TAS-365 per-punch history modal. Pure (tenant zone comes from config).
 */

/** ISO instant → "Oct 5 · 7:02a" (or just "7:02a") in the tenant's clock. */
export function fmtInstant(iso: string, withDate = true): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const zone = tenantTimeZone();
  const d = new Date(t);
  const clock = d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone })
    .toLowerCase()
    .replace(' am', 'a')
    .replace(' pm', 'p');
  if (!withDate) return clock;
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: zone });
  return `${day} · ${clock}`;
}

/** When a change was made: "Oct 5, 2026 · 3:14 PM" in the tenant's clock. */
export function fmtWhen(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const zone = tenantTimeZone();
  const d = new Date(t);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: zone })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: zone })}`;
}

/** Field-aware old/new rendering: ISO datetimes → tenant clock, *Minutes →
 *  hhmm, *Amount/*Rate → money. Everything else verbatim. */
export function fmtLogValue(fieldName: string | null, value: string | null): string {
  if (value == null || value === '') return '—';
  if (fieldName) {
    if (/date.*time|datetime/i.test(fieldName) && !/offset/i.test(fieldName)) {
      const t = Date.parse(value);
      if (!Number.isNaN(t)) return fmtInstant(value);
    }
    if (/minutes/i.test(fieldName)) {
      const n = Number(value);
      if (Number.isFinite(n)) return formatHours(n);
    }
    if (/amount|rate/i.test(fieldName)) {
      const n = Number(value);
      if (Number.isFinite(n)) return money(n);
    }
  }
  return value;
}

/** Date-only columns (EntryDate etc.) arrive as ISO midnight UTC — show the
 *  calendar day, not a shifted instant. Image rendering only. */
export function fmtImageValue(fieldName: string, value: string | null): string {
  if (value != null && /date$/i.test(fieldName) && /^\d{4}-\d{2}-\d{2}(T00:00:00(\.0+)?Z)?$/.test(value)) {
    const t = Date.parse(value);
    if (!Number.isNaN(t)) {
      return new Date(t).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
      });
    }
  }
  return fmtLogValue(fieldName, value);
}

export const KNOWN_SOURCES = new Set(['PAYROLL', 'EMPLOYEE', 'KIOSK', 'WEB', 'SYSTEM']);

/** Badge class for a source; unknown sources get the neutral style. */
export function srcClass(source: string): string {
  return KNOWN_SOURCES.has(source) ? source.toLowerCase() : 'other';
}

export interface ChangeActor {
  source: string;
  adminEmployeeID: number | null;
  adminEmployeeName: string | null;
  employeeID?: number | null;
}

/** Who made a change. Explicit per source — an unrecognised future source must
 *  not masquerade as Payroll. */
export function changeWhoLabel(a: ChangeActor, employeeName: string): string {
  const emp = employeeName || (a.employeeID != null ? `Employee #${a.employeeID}` : 'Employee');
  switch (a.source) {
    case 'PAYROLL':
      return a.adminEmployeeName ?? (a.adminEmployeeID != null ? `Admin #${a.adminEmployeeID}` : 'Payroll');
    case 'EMPLOYEE':
      return emp;
    case 'KIOSK':
      return `${emp} · kiosk`;
    case 'WEB':
      // The employee's own write from the Admin App employee portal.
      return `${emp} · web`;
    case 'SYSTEM':
      return 'System';
    default:
      return a.adminEmployeeName ?? `Unknown source (${a.source || 'blank'})`;
  }
}
